import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { IUserInfo, ICreatUserModel } from "../../user_management/UserManagementModels";
import {
  getUsers,
  createUser as createUserApi,
  updateUserStatus as updateUserStatusApi,
  updateUserRole as updateUserRoleApi,
  updateUserSites as updateUserSitesApi,
} from "../../user_management/UserManagementAPI";
import { RootState } from "../Store";
import { LoadingState } from "../LoadingState";
import { UserAccessType } from "../../user/Models";
import { beginRequest, completeRequest, rejectRequest, RequestState } from "./RequestState";

interface IUserManagementState {
  users: IUserInfo[];
  loadingState: LoadingState;
  errorMessage?: string | null;
  successMessage?: string | null;
  isFetchingUsers: boolean;
  usersRequest?: RequestState;
}

const initialState: IUserManagementState = {
  users: [],
  loadingState: LoadingState.None,
  errorMessage: undefined,
  successMessage: undefined,
  isFetchingUsers: false,
};

export const userManagementSlice = createSlice({
  name: "userManagement",
  initialState,
  reducers: {},
  extraReducers: (builder) =>
    builder
      .addCase(fetchUsers.pending, (state, action) => {
        state.usersRequest ??= { loadingState: LoadingState.None };
        beginRequest(state.usersRequest, action.meta.requestId);
        state.loadingState = LoadingState.Pending;
        state.isFetchingUsers = true;
        state.errorMessage = undefined;
        state.successMessage = undefined;
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        if (!state.usersRequest || !completeRequest(state.usersRequest, action.meta.requestId)) {
          return;
        }
        state.loadingState = LoadingState.Complete;
        state.isFetchingUsers = false;
        state.users = action.payload;
        state.errorMessage = undefined;
        state.successMessage = undefined;
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        if (!state.usersRequest || !rejectRequest(state.usersRequest, action)) {
          return;
        }
        state.loadingState = state.usersRequest.loadingState;
        state.isFetchingUsers = false;
        state.errorMessage = action.meta.aborted ? undefined : "Failed to load users";
        state.successMessage = undefined;
      })
      .addCase(createUser.pending, (state) => {
        state.loadingState = LoadingState.Pending;
        state.errorMessage = undefined;
        state.successMessage = undefined;
      })
      .addCase(createUser.fulfilled, (state, action) => {
        state.loadingState = LoadingState.Complete;
        state.errorMessage = undefined;
        const displayName = (action.meta.arg as ICreatUserModel)?.displayName;
        state.successMessage = displayName ? `User '${displayName}' added` : "User added";
      })
      .addCase(createUser.rejected, (state) => {
        state.loadingState = LoadingState.Error;
        state.errorMessage = "Failed to add user";
        state.successMessage = undefined;
      })
      .addCase(updateUserStatus.pending, (state) => {
        state.loadingState = LoadingState.Pending;
        state.errorMessage = undefined;
        state.successMessage = undefined;
      })
      .addCase(updateUserStatus.fulfilled, (state, action) => {
        state.loadingState = LoadingState.Complete;
        state.errorMessage = undefined;
        const payload = action.payload as { userId: string; enabled: boolean } | undefined;
        const enabled = payload?.enabled;
        const userId = payload?.userId;
        // Update the user's accountEnabled status in the state
        // Microsoft Graph API does return the updated user object with 10 seconds delay.
        if (userId !== undefined && enabled !== undefined) {
          state.users = state.users.map((u) =>
            u.id === userId ? { ...u, accountEnabled: enabled } : u
          );
        }
        state.successMessage = enabled ? "User enabled" : "User disabled";
      })
      .addCase(updateUserStatus.rejected, (state) => {
        state.loadingState = LoadingState.Error;
        state.errorMessage = "Failed to update user status";
        state.successMessage = undefined;
      })
      .addCase(updateUserRole.pending, (state) => {
        state.loadingState = LoadingState.Pending;
        state.errorMessage = undefined;
        state.successMessage = undefined;
      })
      .addCase(updateUserRole.fulfilled, (state, action) => {
        state.loadingState = LoadingState.Complete;
        state.errorMessage = undefined;
        const payload = action.payload as { userId: string; role: UserAccessType } | undefined;
        const role = payload?.role;
        state.successMessage = role
          ? `User role updated to ${UserAccessType[role]}`
          : "User role updated";
      })
      .addCase(updateUserRole.rejected, (state) => {
        state.loadingState = LoadingState.Error;
        state.errorMessage = "Failed to update user role";
        state.successMessage = undefined;
      })
      .addCase(updateUserSites.pending, (state) => {
        state.loadingState = LoadingState.Pending;
        state.errorMessage = undefined;
        state.successMessage = undefined;
      })
      .addCase(updateUserSites.fulfilled, (state) => {
        state.loadingState = LoadingState.Complete;
        state.errorMessage = undefined;
        state.successMessage = "User sites updated";
      })
      .addCase(updateUserSites.rejected, (state) => {
        state.loadingState = LoadingState.Error;
        state.errorMessage = "Failed to update user sites";
        state.successMessage = undefined;
      }),
});

export const fetchUsers = createAsyncThunk("userManagement/fetchUsers", async () => {
  return await getUsers();
});

export const createUser = createAsyncThunk<string, ICreatUserModel>(
  "userManagement/createUser",
  async (user: ICreatUserModel, { dispatch }) => {
    const userId = await createUserApi(user);
    await dispatch(fetchUsers()).unwrap();
    return userId;
  }
);

export const updateUserStatus = createAsyncThunk<
  { userId: string; enabled: boolean },
  { userId: string; enabled: boolean }
>(
  "userManagement/updateUserStatus",
  async ({ userId, enabled }: { userId: string; enabled: boolean }, { dispatch }) => {
    await updateUserStatusApi(userId, enabled);
    await dispatch(fetchUsers()).unwrap();
    return { userId, enabled };
  }
);

export const updateUserRole = createAsyncThunk<
  { userId: string; role: UserAccessType },
  { userId: string; role: UserAccessType }
>(
  "userManagement/updateUserRole",
  async ({ userId, role }: { userId: string; role: UserAccessType }, { dispatch }) => {
    await updateUserRoleApi(userId, role);
    await dispatch(fetchUsers()).unwrap();
    return { userId, role };
  }
);

export const updateUserSites = createAsyncThunk<
  { userId: string; sites: string[] },
  { userId: string; sites: string[] }
>(
  "userManagement/updateUserSites",
  async ({ userId, sites }: { userId: string; sites: string[] }, { dispatch }) => {
    await updateUserSitesApi(userId, sites);
    await dispatch(fetchUsers()).unwrap();
    return { userId, sites };
  }
);

export const selectUsers = (state: RootState) => state.userManagement.users;
export const selectUsersErrorMessage = (state: RootState) => state.userManagement.errorMessage;
export const selectUsersSuccessMessage = (state: RootState) => state.userManagement.successMessage;
export const selectUsersIsFetching = (state: RootState) => state.userManagement.isFetchingUsers;

export const userManagementReducer = userManagementSlice.reducer;
